<app-dialog [(visible)]="visible" (closed)="cancel()" class="max-w-[75vw]" [fullscreenOnMobile]="true">
    <ng-template #headerTemplate>
        <hlm-dialog-header>
            <div class="flex flex-col gap-1">
                <h2 class="text-2xl font-bold text-gray-900 m-0">{{"Checkout" | textCase}}</h2>
                <p class="text-sm text-gray-600 m-0">{{"Set it up in minutes, scale it when you are ready." | textCase}}
                </p>
            </div>
        </hlm-dialog-header>
    </ng-template>

    <ng-template #bodyTemplate>
        <div class="flex-1 overflow-hidden flex flex-col">
            <hr>
            <!-- Scrollable Content -->
            <div class="grid grid-cols-1 lg:grid-cols-2 px-4 py-4 gap-4 overflow-y-auto scrollbar-thin flex-1">
                <!-- Left Column -->
                <div class="flex flex-col gap-4">
                    @if(showPlan()) {
                    <ng-container *ngTemplateOutlet="planTemplate"></ng-container>
                    }
                    @if(currentPlan?.trial?.enabled && showTrial()) {
                    <ng-container *ngTemplateOutlet="trialSetupTemplate"></ng-container>
                    }
                    @if(users().length > 0 || inviteEmails().length > 0) {
                    <ng-container *ngTemplateOutlet="selectedUsersTemplate"></ng-container>
                    }
                    @if(showUsers()) {
                    <ng-container *ngTemplateOutlet="usersTemplate"></ng-container>
                    }
                </div>
                <!-- Right Column -->
                <div class="flex flex-col gap-4">
                    @if(showAddons()) {
                    <ng-container *ngTemplateOutlet="addonsTemplate"></ng-container>
                    }
                    <ng-container *ngTemplateOutlet="orderSummaryTemplate"></ng-container>
                </div>
            </div>
            <hr>
        </div>
    </ng-template>

    <ng-template #footerTemplate>
        <div class="flex justify-end items-center gap-3">
            @if(isDowngrade()) {
            <app-button [label]="'Continue downgrade' | textCase" (clicked)="proceedToPayment()" variant="outlined"
                severity="secondary" [size]="'sm'" [loading]="isSubmitting()" />
            <app-button [label]="'Keep current plan' | textCase" (clicked)="keepCurrentPlan()" variant="filled"
                severity="theme" [size]="'sm'" />
            } @else {
            <app-button [label]="'Cancel' | textCase" (clicked)="cancel()" variant="outlined" severity="secondary"
                [size]="'sm'" />
            <app-button [label]="submitLabel() | textCase" (clicked)="proceedToPayment()" variant="filled"
                severity="theme" [size]="'sm'" [loading]="isSubmitting()" />
            }
        </div>
    </ng-template>
</app-dialog>

<!-- Plan Selection Template -->
<ng-template #planTemplate>
    <div class="card">
        <div class="flex items-center gap-2">
            <app-icon type="layout-grid" size="lg" variant="secondary" source="lucide" />
            <span class="header">{{"Plan" | textCase}}</span>
        </div>
        <div class="card-body">
            <div class="flex gap-3 items-end">
                <div class="flex-1">
                    <span class="text-xs">{{"Plan" | textCase}}</span>
                    <app-select [(value)]="selectedPlan" [options]="filteredPlanOptions" [config]="{
                        placeholder: 'Select a plan',
                        searchable: false,
                        clearable: false
                    }" (onChange)="onPlanChange()">
                    </app-select>
                </div>
                @if(filterByOrgBillingCycle() && checkoutUserOptions().length > 1) {
                <div class="flex-1">
                    <span class="text-xs">{{"Plan change per user" | textCase}}</span>
                    <app-select [value]="selectedComparisonUserId()" (onChange)="onComparisonUserChange($event.value)"
                        [options]="filteredCheckoutUserOptions"
                        [config]="{placeholder: 'Select user', searchable: true, clearable: false}">
                    </app-select>
                </div>
                }
            </div>

            <!-- Current → Switching to banner -->
            @if(comparisonPlan && (selectedPlan || plan())) {
            <div class="flex items-stretch gap-3">
                <div class="flex-1 border border-gray-300 rounded-xl px-3 py-2">
                    <p class="text-xs text-gray-500 m-0">{{"Current" | textCase}}</p>
                    <p class="text-sm font-semibold text-gray-900 m-0">{{ comparisonPlan.name }}</p>
                </div>
                <div class="flex items-center">
                    <app-icon type="arrow-right" source="lucide" size="sm" />
                </div>
                <div class="flex-1 border rounded-xl px-3 py-2"
                    [ngClass]="hasFeaturesLost ? 'border-red-400 bg-red-50' : hasFeaturesGained ? 'border-green-500 bg-green-50' : 'border-gray-300'">
                    <p class="text-xs text-gray-500 m-0">{{"Switching to" | textCase}}</p>
                    <p class="text-sm font-semibold m-0"
                        [ngClass]="hasFeaturesLost ? 'text-red-600' : hasFeaturesGained ? 'text-green-600' : 'text-gray-900'">
                        {{ (selectedPlan || plan())?.name }}
                    </p>
                </div>
            </div>
            }

            <!-- Feature comparison -->
            @if(featureRows.length > 0) {
            <div class="flex flex-col gap-2">
                <span class="text-xs font-semibold text-gray-700">{{"Features" | textCase}}</span>
                <div class="border rounded-xl p-4 flex gap-3"
                    [ngClass]="hasFeaturesLost ? 'border-red-400' : hasFeaturesGained ? 'border-green-500' : 'border-gray-200'">
                    <!-- Left: current plan -->
                    <div class="flex-1 flex flex-col gap-0.5">
                        <span class="text-sm font-semibold text-gray-900 mb-1">{{ comparisonPlan?.name }}</span>
                        @for(row of featureRows; track row.label) {
                        <div class="flex items-center gap-2 px-1.5 py-1 rounded-lg text-sm text-gray-700">
                            <app-icon [type]="row.inCurrent ? 'check' : 'x'" source="lucide" size="sm"
                                [ngClass]="row.inCurrent ? 'text-green-500' : 'text-red-500'" />
                            <span>{{ row.label }}</span>
                        </div>
                        }
                    </div>
                    <!-- Divider -->
                    <div class="w-px bg-gray-200 self-stretch shrink-0"></div>
                    <!-- Right: target plan -->
                    <div class="flex-1 flex flex-col gap-0.5">
                        <span class="text-sm font-semibold mb-1"
                            [ngClass]="hasFeaturesLost ? 'text-red-600' : hasFeaturesGained ? 'text-green-600' : 'text-gray-900'">
                            {{ (selectedPlan || plan())?.name }}
                        </span>
                        @for(row of featureRows; track row.label) {
                        <div class="flex items-center gap-2 px-1.5 py-1 rounded-lg text-sm text-gray-700">
                            <app-icon [type]="row.inTarget ? 'check' : 'x'" source="lucide" size="sm"
                                [ngClass]="row.inTarget ? 'text-green-500' : 'text-red-500'" />
                            <span>{{ row.label }}</span>
                        </div>
                        }
                    </div>
                </div>
                @if(hasFeaturesLost && lostFeaturesCount > 0 && hasActiveOrgPlan()) {
                <div class="flex items-start gap-2 text-red-600 text-sm">
                    <app-icon type="info" source="lucide" size="sm" class="shrink-0 mt-0.5" />
                    <span>You will lose <strong>{{ lostFeaturesCount }} {{ lostFeaturesCount === 1 ? 'feature' :
                            'features' }}</strong>. Downgrade takes effect at the end of your current billing
                        cycle.</span>
                </div>
                } @else if(hasFeaturesGained) {
                <div class="flex items-start gap-2 text-green-600 text-sm">
                    <app-icon type="info" source="lucide" size="sm" class="shrink-0 mt-0.5" />
                    <span>You'll unlock <strong>{{ gainedFeaturesCount }} new {{ gainedFeaturesCount === 1 ? 'feature' :
                            'features' }}</strong> immediately.</span>
                </div>
                }
            </div>
            }

            <!-- Entity Selection - Show available entities for the current plan -->
            @if(availableEntities.length > 0) {
            <div>
                <label class="text-xs">{{"Plan level" | textCase}}</label>
                <!-- Show radio buttons for all available entities -->
                <div class="flex items-center gap-6">
                    @for(entity of availableEntities; track entity) {
                    <label class="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="entity-level" [value]="entity" [(ngModel)]="selectedEntityValue"
                            class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500">
                        <span class="text-sm text-gray-900">{{entity | textCase}}</span>
                    </label>
                    }
                </div>
            </div>
            }

            <!-- Billing Cycle Selection -->
            <div class="flex flex-col">
                <label class="text-xs">{{"Pick a cycle that matches how your team works" | textCase}}</label>
                <app-select-button [(ngModel)]="billingCycle" [options]="termSegmentedOptionsWithPrice"
                    variant="default" severity="ai-light-gradient" (selectionChange)="onBillingCycleChange($event)">
                </app-select-button>
            </div>
        </div>

    </div>
</ng-template>

<!-- Selected Users Template -->
<ng-template #selectedUsersTemplate>
    <div class="card">
        <div class="flex items-center gap-2">
            <app-icon type="users" size="lg" variant="secondary" source="lucide" />
            <span class="header">{{ (inviteEmails().length > 0 ? "Assign to email" : "Assign to users") | textCase
                }}</span>
        </div>
        <div class="card-body">
            @if(inviteEmails().length === 1) {
            <div class="flex items-center gap-2 px-3 py-2 border rounded-base bg-gray-50">
                <app-icon type="mail" source="lucide" size="sm" class="text-gray-500 shrink-0" />
                <span class="text-sm text-gray-700 truncate">{{ inviteEmails()[0] }}</span>
            </div>
            } @else if(inviteEmails().length > 1) {
            <app-multi-select [options]="inviteEmailOptions()" [(value)]="selectedCheckoutUserIds"
                [config]="{placeholder: 'Select emails', filter: true, style: {width: '100%'}, styleClass: 'border', appendTo: 'body'}"
                optionLabel="label" optionValue="value">
            </app-multi-select>
            } @else {
            <app-multi-select [options]="checkoutUserOptions()" [(value)]="selectedCheckoutUserIds"
                [config]="{placeholder: 'Select users', filter: true, style: {width: '100%'}, styleClass: 'border', appendTo: 'body'}"
                optionLabel="label" optionValue="value" [itemTemplate]="userItemTemplate">
            </app-multi-select>
            <ng-template #userItemTemplate let-option>
                <app-user-name-display [user]="option.data" variant="avatar-name" size="sm" />
            </ng-template>
            }
        </div>
    </div>
</ng-template>

<!-- Trial Setup Template -->
<ng-template #trialSetupTemplate>
    <div class="card">
        <div class="flex items-center gap-2">
            <app-icon type="shapes" size="lg" variant="secondary" source="lucide" />
            <span class="header">{{"Trial setup" | textCase}}</span>
        </div>

        <div class="card-body">
            <div class="flex items-center gap-2">
                <app-icon type="circle-check" size="default" variant="default" source="lucide" />
                <span class="text-sm text-gray-700">{{"Trial ends in" | textCase}} {{ trialDays }} {{"days" |
                    textCase}}</span>
            </div>
            <div class="flex items-center gap-2">
                <app-icon type="circle-check" size="default" variant="default" source="lucide" />
                <span class="text-sm text-gray-700">{{"Subscription starts from" | textCase}} {{ subscriptionStartDate
                    }}</span>
            </div>
        </div>
    </div>
</ng-template>

<!-- Users Template -->
<ng-template #usersTemplate>
    <div class="card">
        <div class="flex items-center gap-2 mb-4">
            <app-icon type="users" size="lg" variant="secondary" source="lucide" />
            <span class="header">{{"Users" | textCase}}</span>
        </div>

        <div class="card-body">
            <div class="mb-3">
                <label class="block text-sm font-medium text-gray-700 mb-2">{{"Number of users" | textCase}}</label>
                <div class="flex items-center gap-3">
                    <app-button (clicked)="decrementUsers()" variant="outlined" severity="contrast" [icon]="'minus'"
                        [disabled]="numberOfUsers <= minUsers" [iconSource]="'lucide'" [size]="'sm'" />
                    <app-input type="number" [(ngModel)]="numberOfUsers" [min]="minUsers" [max]="maxUsers || undefined"
                        size="sm" class="text-center" (blur)="onNumberOfUsersBlur()">
                    </app-input>
                    <app-button (clicked)="incrementUsers()" variant="outlined" severity="contrast" [icon]="'plus'"
                        [iconSource]="'lucide'" [disabled]="maxUsers !== null && numberOfUsers >= maxUsers"
                        [size]="'sm'" />
                </div>
            </div>

            <p class="text-xs text-gray-600 mb-2">
                {{"Min" | textCase}} {{ minUsers }},
                {{"max" | textCase}} {{ maxUsers !== null ? maxUsers : 'Unlimited' }}
                ({{"adjustable based on plan" | textCase}})
            </p>

            <!-- Show different calculation based on entity type -->
            @if(selectedEntity() === EntityUnit.USER) {
            <p class="text-sm font-medium text-gray-900">
                {{ numberOfUsers }} {{"users" | textCase}} x ${{ pricePerUser }} = ${{ getPlanTotal()
                }}/{{getTermLabel(billingCycle) | lowercase}}
            </p>
            } @else {
            <p class="text-sm font-medium text-gray-900">
                ${{ pricePerUser }}/{{getTermLabel(billingCycle) | lowercase}} {{"(flat rate for organization)" |
                textCase}}
            </p>
            }
        </div>
    </div>
</ng-template>

<!-- Add-ons Template -->
<ng-template #addonsTemplate>
    <div class="card theme-border">
        <div class="flex items-center gap-2 mb-4">
            <app-icon type="circle-plus" size="lg" variant="secondary" source="lucide" />
            <span class="header">{{"Add-ons" | textCase}}</span>
        </div>

        <div class="card-body">
            <!-- Fixed fees: Setup, Training, AI Education -->
            @for(addon of [planAddons.setupFee, planAddons.trainingFee, planAddons.aiEducationFee]; track addon?.id) {
            @if(addon) {
            <div class="flex flex-col gap-1">
                <!-- Name, Toggle, and Price Row -->
                <div class="flex items-center justify-between gap-3">
                    <div class="flex flex-col">
                        <div class="flex items-center gap-2">
                            <span class="subheader">
                                {{ addon.name }}
                            </span>
                            <app-toggle [(ngModel)]="addon.enabled" [disabled]="isAddonDisabled(addon)"
                                [inputId]="addon.id"
                                (onChange)="onAddonToggleChange(addon.id === 'setup' ? 'setupFee' : addon.id === 'training' ? 'trainingFee' : 'aiEducationFee', $event)">
                            </app-toggle>
                        </div>
                        <!-- Description Row -->
                        <p class="text-xs text-gray-600 m-0 ml-0">{{ addon.description }}</p>
                    </div>
                    <span class="subheader flex-shrink-0">${{ addon.price }}</span>
                </div>
            </div>
            }
            }

            <!-- Custom Fees -->
            @for(addon of planAddons.customFees; track addon.id; let i = $index) {
            @if(addon) {
            <div class="flex flex-col gap-1">
                <div class="flex items-center justify-between gap-3">
                    <div class="flex flex-col">
                        <div class="flex items-center gap-2">
                            <span class="subheader">{{ addon.name }}</span>
                            <app-toggle [(ngModel)]="addon.enabled" [disabled]="isAddonDisabled(addon)"
                                [inputId]="addon.id" (onChange)="onCustomFeeToggle(i, $event)">
                            </app-toggle>
                        </div>
                        @if(addon.description) {
                        <p class="text-xs text-gray-600 m-0 ml-0">{{ addon.description }}</p>
                        }
                    </div>
                    <span class="subheader flex-shrink-0">${{ addon.price }}</span>
                </div>
            </div>
            }
            }
        </div>
    </div>
</ng-template>

<!-- Order Summary Template -->
<ng-template #orderSummaryTemplate>
    <div class="flex flex-col gap-3">
        <span class="header">{{"Your order" | textCase}}</span>

        <div class="card border-none theme-bg-light">
            <div class="card-body">

                @if(source() === CheckoutSource.INVITE && inviteMembers().length > 0) {
                <!-- Invite flow: per-user plan breakdown -->
                <span class="header">{{"Invite summary" | textCase}}</span>
                <div class="flex flex-col gap-1 text-sm">
                    <div class="flex items-center gap-2">
                        <app-icon type="circle-check" size="default" variant="default" source="lucide" />
                        <span class="text-sm text-gray-700">{{"Billed" | textCase}}</span>
                        <span class="ml-auto text-sm text-gray-700">{{ getFrequencyLabel() }}</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <app-icon type="circle-check" size="default" variant="default" source="lucide" />
                        <span class="text-sm text-gray-700">{{"Subscription" | textCase}}</span>
                        <span class="ml-auto text-sm text-gray-700">{{ subscriptionStartDate }}</span>
                    </div>
                </div>
                <hr>

                <!-- Per-user plan rows -->
                <div class="flex flex-col gap-1">
                    <div class="flex items-center justify-between text-xs font-medium text-gray-500 px-1 mb-1">
                        <span>Email</span>
                        <span>Plan / Price</span>
                    </div>
                    @for(item of inviteLineItems; track item.email) {
                    <div class="flex items-center justify-between py-1 px-1 rounded-base hover:bg-gray-50">
                        <div class="flex items-center gap-1.5 min-w-0">
                            <app-icon type="mail" source="lucide" size="sm" class="text-gray-400 shrink-0" />
                            <span class="text-xs text-gray-700 truncate">{{ item.email }}</span>
                        </div>
                        <div class="flex items-center gap-2 shrink-0 ml-2">
                            <span class="text-xs text-blue-700 font-medium">{{ item.planName }}</span>
                            <span class="text-xs text-gray-500">${{ item.price }}/{{ getTermLabel(billingCycle) |
                                lowercase }}</span>
                        </div>
                    </div>
                    }
                </div>
                <hr>

                <!-- Invite Total -->
                <div class="">
                    <div class="flex justify-between items-center mb-1">
                        <span class="header">{{"Total" | textCase}}</span>
                        <span class="header">${{ getInviteTotal() }} /{{ getTermLabel(billingCycle) | lowercase
                            }}</span>
                    </div>
                    <div class="flex justify-between items-center">
                        <span class="text-xs text-gray-600">{{"Next renewal on" | textCase}} {{ renewalDate }}</span>
                        <span class="text-xs text-gray-600">({{"Tax exclusive" | textCase}})</span>
                    </div>
                </div>

                } @else {
                <!-- Default flow -->
                <span class="header">{{ selectedPlan?.name}}</span>
                <!-- Plan Details -->
                <div class="flex flex-col gap-1 text-sm">
                    <div class="flex items-center gap-2">
                        <app-icon type="circle-check" size="default" variant="default" source="lucide" />
                        <span class="text-sm text-gray-700">{{"Billed" | textCase}}</span>
                        <span class="ml-auto text-sm text-gray-700">{{ getFrequencyLabel() }}</span>
                    </div>
                    @if(selectedEntity() === EntityUnit.USER) {
                    <div class="flex items-center gap-2">
                        <app-icon type="circle-check" size="default" variant="default" source="lucide" />
                        <span class="text-sm text-gray-700">{{"Users" | textCase}}</span>
                        <span class="ml-auto text-sm text-gray-700">{{ source() === CheckoutSource.INVITE ?
                            inviteEmails().length : selectedCheckoutUserIds.length > 0 ? selectedCheckoutUserIds.length
                            : numberOfUsers }}</span>
                    </div>
                    }
                    @if(currentPlan?.trial?.enabled && showTrial()) {
                    <div class="flex items-center gap-2">
                        <app-icon type="circle-check" size="default" variant="default" source="lucide" />
                        <span class="text-sm text-gray-700">{{"Trial" | textCase}}</span>
                        <span class="ml-auto text-sm text-gray-700">{{ trialDays }} {{"days" | textCase}}</span>
                    </div>
                    }
                    <div class="flex items-center gap-2">
                        <app-icon type="circle-check" size="default" variant="default" source="lucide" />
                        <span class="text-sm text-gray-700">{{"Subscription" | textCase}}</span>
                        <span class="ml-auto text-sm text-gray-700">{{ subscriptionStartDate }}</span>
                    </div>
                </div>
                <hr>

                <!-- Plan Total -->
                <div class="flex justify-between items-center">
                    <span class="subheader">{{"Plan total" | textCase}}</span>
                    <span class="subheader">${{ pricePerUser }} /{{getTermLabel(billingCycle) | lowercase}}</span>
                </div>

                <!-- Prorated charge for org upgrade -->
                @if(source() === CheckoutSource.ORG_PLAN_CHANGE && !isDowngrade()) {
                <div class="flex flex-col gap-1 mt-1">
                    <div class="flex justify-between items-center">
                        <span class="text-sm text-gray-700">{{"Amount due today (prorated)" | textCase}}</span>
                        @if(isLoadingProratedCharge()) {
                        <span class="text-sm text-gray-400">{{"Calculating..." | textCase}}</span>
                        } @else {
                        <span class="text-sm font-semibold text-gray-900">${{ (proratedCharge() ?? 0) | number:'1.2-2'
                            }}</span>
                        }
                    </div>
                    <p class="text-xs text-gray-500 m-0">{{"Covers remaining days in current billing cycle. Full amount
                        charged from next cycle." | textCase}}</p>
                </div>
                }

                <hr>

                <!-- Add-ons -->
                @if(hasEnabledAddons()) {
                <div class="">
                    <span class="subheader">{{"Add ons" | textCase}}</span>
                    <div class="flex flex-col gap-1">
                        @for(addon of [planAddons.setupFee, planAddons.trainingFee, planAddons.aiEducationFee]; track
                        addon?.id) {
                        @if(addon && addon.enabled) {
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <app-icon type="circle-plus" size="lg" variant="secondary" source="lucide" />
                                <span class="text-sm text-gray-700">{{ addon.name }}</span>
                            </div>
                            <span class="text-sm font-medium text-gray-900">${{ addon.price }}</span>
                        </div>
                        }
                        }
                        @for(addon of planAddons.customFees; track addon.id) {
                        @if(addon && addon.enabled) {
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <app-icon type="circle-plus" size="lg" variant="secondary" source="lucide" />
                                <span class="text-sm text-gray-700">{{ addon.name }}</span>
                            </div>
                            <span class="text-sm font-medium text-gray-900">${{ addon.price }}</span>
                        </div>
                        }
                        }
                    </div>
                </div>
                <hr>
                }

                <!-- Downgrade notice -->
                @if(isDowngrade() && hasActiveOrgPlan()) {
                <div class="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                    <app-icon type="info" size="default" variant="default" source="lucide"
                        class="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div class="flex flex-col gap-0.5">
                        <span class="text-xs font-medium text-amber-800">{{"Downgrade scheduled" | textCase}}</span>
                        <span class="text-xs text-amber-700">{{"This downgrade will take effect at the end of your
                            current billing cycle. The new pricing will apply from the next billing cycle." |
                            textCase}}</span>
                    </div>
                </div>
                }

                <!-- Total -->
                <div class="">
                    <div class="flex justify-between items-center mb-1">
                        <span class="header">{{"Total" | textCase}}</span>
                        <span class="header">${{ getTotal() }} /{{getTermLabel(billingCycle) | lowercase}}</span>
                    </div>
                    <div class="flex justify-between items-center">
                        @if(isDowngrade() && hasActiveOrgPlan()) {
                        <span class="text-xs text-amber-600">{{"Effective from next billing cycle" | textCase}}</span>
                        } @else if(!isDowngrade() && hasActiveOrgPlan()) {
                        <span class="text-xs text-gray-600">{{"Next renewal on" | textCase}} {{ renewalDate }}</span>
                        }
                        <span class="text-xs text-gray-600">({{"Tax exclusive" | textCase}})</span>
                    </div>
                </div>
                }

            </div>
        </div>
    </div>
</ng-template>